const WORKER_URL = 'https://mga-standalone-dev.muokkis.workers.dev';
import {requestOfferJson, cartOfferMutationLock, observeOfferExposure} from './mga-cart-offers.js';
const SELECTOR = '[data-mga-copy-offer]';
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const TOKEN = /^(?:\d{10,16}|\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?Z)\.[a-f0-9]{64}$/i;
const QA_SHOP = 'mga-checkout-plus-qa.myshopify.com';

// Checkout image map. Keep these rules equal to shared/checkout-image-map.js and the Cart Transform.
export const IMAGE_ATTRIBUTE = '_mga_checkout_images';
const IMAGE_URL = /^https:\/\/cdn\.shopify\.com\/s\/files\/\d+\/\d+\/\d+(?:\/\d+)?\/files\/mga-checkout-[a-f0-9]{32}\.webp(?:\?v=\d+)?$/;
const PREVIEW_ID = /\/([A-Za-z0-9_-]{4,80})\.webp(?:\?[^#\s]*)?$/;
const PROOF_ID = /^(?:tcp|scp)_[a-f0-9]{64}$/;
const DAY_MS = 86400000;

export function imageValidUntil(value, now = Date.now()) {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [year, month, day] = value.split('-').map(Number);
  if (year < 2000 || year > 9999) return false;
  const date = new Date(Date.UTC(year, month - 1, day));
  if (date.toISOString().slice(0, 10) !== value) return false;
  const current = Math.floor(now / DAY_MS), expiry = date.getTime() / DAY_MS;
  return expiry > current && expiry <= current + 44;
}

export function lineImageIdentity(properties) {
  const designId = String(properties?._mga_design_id || '');
  const match = PREVIEW_ID.exec(String(properties?._mga_preview_url || ''));
  const emailPreview = String(properties?._mga_email_preview || '');
  if (!designId || designId.length > 128 || !match || !emailPreview) return null;
  const previewId = match[1];
  // The confirmed proof, or otherwise the customer's own design (owner decision 2026-10-09).
  return {designId, previewId, proof: PROOF_ID.test(previewId)};
}

export function lineImageRequest(properties) {
  const identity = lineImageIdentity(properties);
  const token = String(properties?._mga_token || '');
  if (!identity || !token || token.length > 200) return null;
  return identity.proof
    ? {designId: identity.designId, token, cartPreviewId: identity.previewId, recipeSignature: String(properties?._mga_text_signature || '')}
    : {designId: identity.designId, token};
}

function imageEntries(value, now) {
  if (typeof value !== 'string' || !value || value.length > 4000) return [];
  let parsed;
  try { parsed = JSON.parse(value); } catch { return []; }
  if (parsed?.v !== 1 || !Array.isArray(parsed.e)) return [];
  return parsed.e.slice(-10).filter(entry => Array.isArray(entry) && entry.length === 4 && entry.every(item => typeof item === 'string')
    && entry[0] && entry[0].length <= 128 && /^[A-Za-z0-9_-]{4,80}$/.test(entry[1]) && entry[2].length <= 2048
    && IMAGE_URL.test(entry[2]) && imageValidUntil(entry[3], now));
}

export function hasLinkedImage(value, identity, now = Date.now()) {
  return !!identity && imageEntries(value, now).some(entry => entry[0] === identity.designId && entry[1] === identity.previewId);
}

export function mergeLinkedImage(value, identity, image, now = Date.now()) {
  const entry = [identity?.designId, identity?.previewId, image?.imageUrl, image?.validUntil];
  if (!identity || typeof entry[2] !== 'string' || entry[2].length > 2048 || !IMAGE_URL.test(entry[2]) || !imageValidUntil(entry[3], now)) return null;
  const current = imageEntries(value, now);
  const existing = current.find(item => item[0] === entry[0] && item[1] === entry[1]);
  if (existing && existing[2] === entry[2] && existing[3] === entry[3]) return null;
  const entries = [...current.filter(item => item !== existing), entry].slice(-10);
  let serialized = JSON.stringify({v: 1, e: entries});
  while (serialized.length > 4000 && entries.length > 1) { entries.shift(); serialized = JSON.stringify({v: 1, e: entries}); }
  return serialized.length <= 4000 ? serialized : null;
}

/** Fill missing checkout images for the lines in this cart. Never touches a line; one attribute write per image. */
export function createImageLinker({getConfig, getCart, updateAttribute, prepare, wait = ms => new Promise(resolve => setTimeout(resolve, ms)), now = () => Date.now()}) {
  const started = new Set();
  let writes = Promise.resolve();
  function link(identity, image) {
    writes = writes.then(async () => {
      const cart = await getCart();
      const next = mergeLinkedImage(cart?.attributes?.[IMAGE_ATTRIBUTE], identity, image, now());
      if (next) await updateAttribute(next);
    }).catch(() => {});
    return writes;
  }
  async function fill(line) {
    const input = lineImageRequest(line.properties);
    const identity = lineImageIdentity(line.properties);
    let expectedSourceRevision;
    for (const delay of [0, 1500, 2500, 4000, 6000, 8000, 10000]) {
      if (delay) await wait(delay);
      let value;
      try { value = await prepare({...input, ...(expectedSourceRevision ? {expectedSourceRevision} : {})}); } catch { continue; }
      if (!value || value.stop) return;
      if (!/^[a-f0-9]{64}$/.test(value.sourceRevision || '') || (expectedSourceRevision && value.sourceRevision !== expectedSourceRevision)) return;
      expectedSourceRevision = value.sourceRevision;
      if (value.status === 'ready') { await link(identity, value); return; }
      if (value.status !== 'pending') return;
    }
  }
  return {
    async run() {
      const [config, cart] = await Promise.all([getConfig(), getCart()]);
      if (config?.artworkAssetsEnabled !== true) return 0;
      const value = cart?.attributes?.[IMAGE_ATTRIBUTE];
      const missing = (cart?.items || []).filter(line => line?.properties && lineImageRequest(line.properties)
        && !hasLinkedImage(value, lineImageIdentity(line.properties), now()));
      const jobs = [];
      for (const line of missing.slice(0, 5)) {
        const identity = lineImageIdentity(line.properties);
        const key = `${identity.designId}|${identity.previewId}`;
        if (started.has(key)) continue;
        started.add(key);
        jobs.push(() => fill(line));
      }
      // One preparation at a time keeps a large cart polite to the Worker.
      for (const job of jobs) await job();
      return jobs.length;
    }
  };
}

export function copyConfiguration(value) {
  const copy = value?.exactCopy;
  return value?.version === 1 && value.availability?.exact_copy === true
    && copy?.percent === 10 && copy.maxDiscountedExtraUnits === 1 && copy.requiredMarker === 'exact_copy_v1'
    && Array.isArray(copy.collectionIds) && copy.collectionIds.length > 0 && copy.collectionIds.length <= 100
    && copy.collectionIds.every(id => /^gid:\/\/shopify\/Collection\/[1-9]\d{0,19}$/.test(id))
    && Array.isArray(copy.excludedVariantIds) && copy.excludedVariantIds.length <= 100
    && copy.excludedVariantIds.every(id => /^gid:\/\/shopify\/ProductVariant\/[1-9]\d{0,19}$/.test(id)) ? value : null;
}

export function configurationIdentity(config) {
  return config && JSON.stringify([config.profile || 'mga', config.validUntil || null, config.exactCopy.percent, config.exactCopy.maxDiscountedExtraUnits,
    config.exactCopy.requiredMarker, [...config.exactCopy.collectionIds].sort(), [...config.exactCopy.excludedVariantIds].sort()]);
}
export function configMatchesShop(value, shop, now = Date.now()) {
  if (value?.shopDomain !== shop) return false;
  if (shop === 'bihgbd-ts.myshopify.com') return value.profile === 'mga' && value.validUntil === null;
  const expiry = Date.parse(value?.validUntil || '');
  return shop === QA_SHOP && value?.profile === 'mga-dev-qa'
    && Number.isFinite(expiry) && expiry > now && expiry - now <= 86400000;
}

export function cartLineIdentity(line) {
  if (!line || !/^[1-9]\d{0,19}$/.test(String(line.variant_id)) || !line.properties
    || typeof line.properties !== 'object' || Array.isArray(line.properties)) return null;
  const entries = Object.entries(line.properties);
  if (entries.length > 120 || entries.some(([key,value]) => !key || key.length > 100 || typeof value !== 'string' || value.length > 8192)) return null;
  entries.sort(([a],[b]) => a.localeCompare(b));
  return JSON.stringify([String(line.variant_id), String(line.product_id), line.selling_plan_allocation || null,
    line.parent_relationship || null, line.item_components || line.line_components || [], entries]);
}

export function uniqueCartLine(cart, identity) {
  const matches = (cart?.items || []).filter(line => cartLineIdentity(line) === identity);
  return matches.length === 1 ? matches[0] : null;
}

export function eligibleCartLine(line, metadata, config) {
  return !!copyConfiguration(config) && line?.quantity === 1 && line.requires_shipping === true
    && String(line.variant_id) === metadata.variantId && String(line.product_id) === metadata.productId
    && !line.selling_plan_allocation && !line.parent_relationship && !line.has_components
    && !(line.item_components || []).length && !(line.line_components || []).length
    && !String(line.sku || '').toUpperCase().startsWith('DIG-')
    && !config.exactCopy.excludedVariantIds.includes(`gid://shopify/ProductVariant/${line.variant_id}`)
    && metadata.collectionIds.some(id => config.exactCopy.collectionIds.includes(`gid://shopify/Collection/${id}`))
    && line.properties?._mga_copy_offer === 'exact_copy_v1' && !line.properties._mga_addon_kind
    && typeof line.properties._mga_design_id === 'string' && line.properties._mga_design_id.length > 0
    && line.properties._mga_design_id.length <= 128 && TOKEN.test(line.properties._mga_token || '')
    && cartLineIdentity(line) !== null;
}

/** The offer is shown only while the cart holds one single piece (owner decision 2026-10-09).
 * A matching photo print is its own add-on, not a second piece (owner decision 2026-10-10). */
export function singleItemCart(cart) {
  const addonUnits = (cart?.items || []).filter(line => String(line?.sku || '').toUpperCase().startsWith('PRT-')
    || line?.properties?._mga_addon_kind).reduce((total, line) => total + (Number(line.quantity) || 0), 0);
  return Number(cart?.item_count) - addonUnits === 1;
}

export function createCopyController({getConfig, getCart, changeCart, canMutate = () => true}) {
  let busy = false;
  const blocked = new Set();
  return {
    isBusy: () => busy,
    isBlocked: identity => blocked.has(identity),
    async add(ready, sections = []) {
      if (busy || blocked.has(ready?.identity)) return {status: 'blocked'};
      busy = true;
      try {
        const config = copyConfiguration(await getConfig());
        if (!config || configurationIdentity(config) !== configurationIdentity(ready.config)) return {status: 'unavailable'};
        const current = await getCart();
        const line = uniqueCartLine(current, ready.identity);
        if (!line || !singleItemCart(current) || !eligibleCartLine(line, ready.metadata, config) || !canMutate(line)) return {status: 'unavailable'};
        let cart;
        blocked.add(ready.identity);
        try {
          cart = await changeCart({id: line.key, quantity: 2, ...(sections.length ? {sections} : {})});
        } catch {
          try { cart = await getCart(); } catch { return {status: 'uncertain'}; }
        }
        const after = uniqueCartLine(cart, ready.identity);
        return after?.quantity === 2 ? {status: 'accepted', cart, key: after.key} : {status: 'uncertain', cart};
      } catch { return {status: 'unavailable'}; }
      finally { busy = false; }
    }
  };
}

export function analyticsAllowed(win) {
  try {
    const privacy = win?.Shopify?.customerPrivacy;
    return win?.MGA_TRACKING_DISABLED !== true && privacy?.analyticsProcessingAllowed?.call(privacy) === true;
  } catch { return false; }
}

export async function finishCopyRendering(result, render, reload) {
  if (result.status !== 'accepted') return;
  try { if (render(result.cart, result.key)) return; } catch { }
  reload();
}

async function requestJson(url, options = {}) {
  return requestOfferJson(url, options);
}

function startCopyOffers() {
  const owned = location.protocol === 'https:' && (['myguardianart.com', 'www.myguardianart.com',
    'bihgbd-ts.myshopify.com', QA_SHOP].includes(location.hostname));
  if (!owned) return;
  const root = window.Shopify?.routes?.root || '/';
  const getCart = () => requestJson(`${root}cart.js`, {cache: 'no-store', dedupe: !mutation.isBusy()});
  const getConfig = async () => {
    const shop = document.querySelector(SELECTOR)?.dataset.shop || '';
    const config = await requestJson(`${WORKER_URL}/checkout-offers/config?shop=${encodeURIComponent(shop)}`, {cache: 'no-store'});
    return configMatchesShop(config, shop) ? config : null;
  };
  let nativePendingUntil = 0;
  const mutation = cartOfferMutationLock();
  const cartArea = () => document.getElementById('main-cart');
  const controller = createCopyController({getConfig, getCart, changeCart: body => requestJson(`${root}cart/change.js`, {
    method: 'POST', headers: {'Content-Type': 'application/json'}, body: JSON.stringify({...body, sections_url: location.pathname})
  }), canMutate: () => Date.now() >= nativePendingUntil});
  const readyCards = new WeakMap();
  const viewed = new Set();
  const notices = new Map();
  const NOTICE_MS = 6000;

  function announce(text) {
    let region = document.getElementById('mga-copy-offer-live');
    if (!region) {
      region = document.createElement('p');
      region.id = 'mga-copy-offer-live';
      region.className = 'visually-hidden';
      region.setAttribute('role', 'status');
      region.setAttribute('aria-live', 'polite');
      document.body.appendChild(region);
    }
    region.textContent = '';
    setTimeout(() => { region.textContent = text; }, 50);
  }

  function showLineNotice(container, text) {
    if (!container || !text) return;
    const notice = document.createElement('p');
    notice.className = 'mga-copy-offer__status mga-copy-offer__status--added';
    notice.textContent = text;
    container.appendChild(notice);
    setTimeout(() => notice.remove(), NOTICE_MS);
  }
  let refreshPromise;
  let refreshPending = false;
  let refreshTimer;
  let sessionId;
  let sequence = 0;

  function track(eventName, metadata) {
    if (!analyticsAllowed(window)) return;
    try {
      if (!sessionId) {
        const stored = sessionStorage.getItem('mga_funnel_session_v1:cart');
        sessionId = UUID.test(stored || '') ? stored : crypto.randomUUID();
        sessionStorage.setItem('mga_funnel_session_v1:cart', sessionId);
      }
      const event = {event_id: crypto.randomUUID(), event_name: eventName, occurred_at: Date.now(),
        event_sequence: ++sequence, funnel_session_id: sessionId, page_path: location.pathname,
        properties: {offer_id: 'exact_copy_v1', offer_version: '1', offer_kind: 'exact_copy',
          offer_placement: metadata.placement, quantity_before: 1, quantity_after: eventName === 'checkout_offer_accepted' ? 2 : 1,
          analytics_allowed: true, event_source: 'theme', tracking_version: 'funnel-v2'}};
      void fetch(`${WORKER_URL}/track-funnel`, {method: 'POST', headers: {'Content-Type': 'text/plain;charset=UTF-8'},
        body: JSON.stringify({events: [event]}), keepalive: true}).catch(() => {});
    } catch { }
  }

  function metadataFor(card) {
    return {variantId: card.dataset.variantId, productId: card.dataset.productId,
      collectionIds: (card.dataset.collections || '').split(','), placement: card.dataset.placement};
  }

  function visible(card) {
    const dialog = card.closest('cart-notification');
    return !dialog || dialog.querySelector('#cart-notification')?.classList.contains('active');
  }

  function observeView(card, ready) {
    if (!analyticsAllowed(window) || !visible(card) || viewed.has(ready.identity)) return;
    observeOfferExposure(card, () => {
      if (!analyticsAllowed(window) || !visible(card)) return false;
      if (!viewed.has(ready.identity)) { viewed.add(ready.identity); track('checkout_offer_viewed', ready.metadata); }
      return true;
    });
  }

  async function refresh() {
    if (mutation.isBusy()) return;
    if (refreshPromise) { refreshPending = true; return refreshPromise; }
    if (!document.querySelector(SELECTOR)) return;
    refreshPromise = (async () => {
      let cards = [];
      try {
        const [rawConfig, cart] = await Promise.all([getConfig(), getCart()]);
        if (mutation.isBusy()) return;
        cards = [...document.querySelectorAll(SELECTOR)];
        const config = copyConfiguration(rawConfig);
        for (const card of cards) {
          const notice = notices.get(card.dataset.lineKey);
          if (notice && notice.until > Date.now()) {
            const status = card.querySelector('.mga-copy-offer__status');
            status.textContent = notice.text;
            status.hidden = false;
            card.querySelector('[data-mga-copy-add]').hidden = true;
            card.hidden = false;
            readyCards.delete(card);
            continue;
          }
          if (notice) notices.delete(card.dataset.lineKey);
          const matches = (cart.items || []).filter(line => line.key === card.dataset.lineKey);
          const line = matches.length === 1 ? matches[0] : null;
          const metadata = metadataFor(card);
          const identity = cartLineIdentity(line);
          const ready = config && identity && singleItemCart(cart) && eligibleCartLine(line, metadata, config) && uniqueCartLine(cart, identity);
          if (!ready) { card.hidden = true; readyCards.delete(card); continue; }
          const details = {identity, config, metadata};
          readyCards.set(card, details);
          card.querySelector('[data-mga-copy-add]').disabled = controller.isBusy() || controller.isBlocked(identity);
          card.hidden = false;
          observeView(card, details);
        }
      } catch { for (const card of document.querySelectorAll(SELECTOR)) { card.hidden = true; readyCards.delete(card); } }
    })().finally(() => {
      refreshPromise = null;
      if (refreshPending) { refreshPending = false; scheduleRefresh(); }
    });
    return refreshPromise;
  }

  function scheduleRefresh() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => void refresh(), 100);
  }

  function renderCart(cart, key) {
    if (!cart?.sections) return false;
    const notification = document.querySelector('cart-notification');
    const cartItems = document.querySelector('cart-items');
    if (cartItems) {
      let rendered = true;
      for (const section of cartItems.getSectionsToRender()) {
        const html = cart.sections[section.section];
        const target = document.getElementById(section.id);
        const replacement = html && new DOMParser().parseFromString(html, 'text/html').querySelector(section.selector);
        if (!target || !replacement) { rendered = false; continue; }
        (target.querySelector(section.selector) || target).innerHTML = replacement.innerHTML;
      }
      cartItems.currentItemCount = cart.item_count;
      return rendered;
    }
    if (notification && key && cart.sections['cart-notification-product'] && cart.sections['cart-icon-bubble']) {
      notification.renderContents({...cart, key});
      return true;
    }
    return false;
  }

  document.addEventListener('click', async event => {
    const button = event.target.closest?.('[data-mga-copy-add]');
    if (!button) return;
    event.preventDefault();
    const card = button.closest(SELECTOR);
    const ready = readyCards.get(card);
    if (!ready || button.disabled) return;
    const area = cartArea();
    const rowQuantity = card.closest('.cart-item')?.querySelector('[name="updates[]"]');
    if (area?.classList.contains('cart__items--disabled') || Date.now() < nativePendingUntil
      || (rowQuantity && Number(rowQuantity.value) !== 1)) return;
    const release = mutation.acquire();
    if (!release) return;
    try {
    track('checkout_offer_clicked', ready.metadata);
    const cartItems = document.querySelector('cart-items');
    const sections = cartItems ? [...new Set(cartItems.getSectionsToRender().map(section => section.section))]
      : ['cart-notification-product', 'cart-icon-bubble'];
    const result = await controller.add(ready, sections);
    const text = card.dataset[result.status === 'accepted' ? 'added' : result.status === 'uncertain' ? 'uncertain' : 'unavailable'];
    const status = card.querySelector('.mga-copy-offer__status');
    status.textContent = text;
    status.hidden = false;
    button.hidden = result.status !== 'blocked';
    if (result.status === 'blocked') return;
    track(result.status === 'accepted' ? 'checkout_offer_accepted' : 'checkout_offer_failed', ready.metadata);
    announce(text);
    if (result.status !== 'accepted') {
      // The card stays; keep its notice through the next refresh and give focus a place to land.
      notices.set(card.dataset.lineKey, {text, until: Date.now() + NOTICE_MS});
      setTimeout(scheduleRefresh, NOTICE_MS + 50);
      status.tabIndex = -1;
      status.focus();
      await refresh();
      return;
    }
    await finishCopyRendering(result, (cart, key) => {
        if (renderCart(cart, key)) {
          const index = result.cart.items.findIndex(line => line.key === result.key);
          // At quantity two the offer card is gone, so the confirmation sits in the line itself.
          showLineNotice(cartItems ? document.querySelector(`#CartItem-${index + 1} .cart-item__details`)
            || document.getElementById(`CartItem-${index + 1}`) : document.getElementById('cart-notification-product'), text);
          const focus = cartItems ? document.getElementById(`Quantity-${index + 1}`)
            : document.querySelector('#cart-notification a[href], #cart-notification button:not([hidden])');
          focus?.focus();
          return true;
        }
        return false;
    }, () => location.reload());
    await refresh();
    } finally { release(); }
  });
  new MutationObserver(records => {
    if (records.some(record => [...record.addedNodes].some(node => node.nodeType === 1
      && (node.matches?.(SELECTOR) || node.querySelector?.(SELECTOR))))) scheduleRefresh();
  }).observe(document.body, {childList: true, subtree: true});
  document.addEventListener('visitorConsentCollected', scheduleRefresh);
  document.addEventListener('mga:cart-offers:changed', scheduleRefresh);
  document.addEventListener('mga:cart-offers:ready', scheduleRefresh);
  document.addEventListener('change', event => {
    if (event.target.matches?.('cart-items [name="updates[]"]')) nativePendingUntil = Date.now() + 1000;
  }, true);
  document.addEventListener('submit', event => {
    if (controller.isBusy() && ['cart', 'cart-notification-form'].includes(event.target.id)) event.preventDefault();
  }, true);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) scheduleRefresh(); });
  window.addEventListener('pageshow', scheduleRefresh);
  void refresh();

  // A buyer who reaches the cart before the product page finished linking the checkout image gets it here.
  const linker = createImageLinker({
    getConfig: async () => {
      const shop = window.Shopify?.shop || document.querySelector(SELECTOR)?.dataset.shop || '';
      const config = await requestJson(`${WORKER_URL}/checkout-offers/config?shop=${encodeURIComponent(shop)}`, {cache: 'no-store'});
      return configMatchesShop(config, shop) ? config : null;
    },
    getCart,
    updateAttribute: value => requestJson(`${root}cart/update.js`, {method: 'POST', headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({attributes: {[IMAGE_ATTRIBUTE]: value}})}),
    prepare: async input => {
      const response = await fetch(`${WORKER_URL}/checkout-assets/prepare`, {method: 'POST', credentials: 'omit',
        headers: {'Content-Type': 'application/json'}, body: JSON.stringify(input), signal: AbortSignal.timeout(8000)});
      if ([400, 403, 404, 409, 413, 415].includes(response.status)) return {stop: true};
      if (!response.ok) throw new Error('Retry later');
      return response.json();
    }
  });
  if (location.pathname.replace(/\/$/, '').endsWith('/cart')) void linker.run().catch(() => {});
}

if (typeof document !== 'undefined') startCopyOffers();
